import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {
  type FC,
  type ReactNode,
  type RefObject,
  useCallback,
  useMemo,
} from "react";

import { RichTooltip } from "~/components/UI/RichTooltip";

import { buildGridTemplateColumns } from "./buildGridTemplateColumns";
import * as Styles from "./DataGridHeader.styles";

export type DataGridHeaderSortDirection = "asc" | "desc";

export interface IDataGridHeaderItem {
  field: string;
  label: string;
  minWidth: number;
  width?: number;
  flex?: number;
  sortable?: boolean;
  pinned?: boolean;
  pinnedLeftPadding?: number;
  sortDirection?: DataGridHeaderSortDirection | null;
  tooltip?: string | null;
  align?: Styles.DataGridHeaderAlign;
  paddingLeft?: number;
  children?: ReactNode;
}

interface IDataGridHeaderProps {
  rootRef?: RefObject<HTMLDivElement | null>;
  items: IDataGridHeaderItem[];
  compact?: boolean;
  onSortChange?: (
    field: string,
    direction: DataGridHeaderSortDirection
  ) => void;
}

const COMPACT_SORT_ICONS = {
  asc: ArrowUpwardIcon,
  desc: ArrowDownwardIcon,
  none: UnfoldMoreIcon,
};

const getSortIcon = (
  direction: DataGridHeaderSortDirection | null,
  compact: boolean
) => {
  if (compact) {
    const Icon = COMPACT_SORT_ICONS[direction ?? "none"];

    return <Icon fontSize="inherit" />;
  }

  const Icon = direction === "desc" ? ArrowDropDownIcon : ArrowDropUpIcon;

  return <Icon fontSize="small" />;
};

export const DataGridHeader: FC<IDataGridHeaderProps> = ({
  rootRef,
  items,
  compact = false,
  onSortChange,
}) => {
  const templateColumns = useMemo(
    () => buildGridTemplateColumns(items),
    [items]
  );

  const handleSortClick = useCallback(
    (item: IDataGridHeaderItem) => {
      if (!item.sortable || !onSortChange) {
        return;
      }

      const nextDirection = item.sortDirection === "asc" ? "desc" : "asc";

      onSortChange(item.field, nextDirection);
    },
    [onSortChange]
  );

  return (
    <Styles.Root
      ref={rootRef}
      templateColumns={templateColumns}
      compact={compact}
    >
      {items.map((item) => {
        return (
          <Styles.HeaderCell
            key={item.field}
            compact={compact}
            pinned={item.pinned}
            pinnedLeftPadding={item.pinnedLeftPadding}
            sortable={item.sortable}
            sorted={!!item.sortDirection}
            align={item.align}
            paddingLeft={item.paddingLeft}
            onClick={item.sortable ? () => handleSortClick(item) : undefined}
          >
            {item.tooltip && (
              <RichTooltip title={item.tooltip}>
                <InfoOutlinedIcon
                  fontSize="small"
                  color="disabled"
                  sx={{ fontSize: compact ? 13 : 14 }}
                />
              </RichTooltip>
            )}

            {item.label && (
              <Styles.HeaderLabel compact={compact}>
                {item.label}
              </Styles.HeaderLabel>
            )}

            {item.sortable && (
              <Styles.SortIndicator
                compact={compact}
                direction={item.sortDirection ?? null}
              >
                {getSortIcon(item.sortDirection ?? null, compact)}
              </Styles.SortIndicator>
            )}

            {item.children}
          </Styles.HeaderCell>
        );
      })}
    </Styles.Root>
  );
};
